import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, ArrowRight, AlertCircle, GraduationCap, Users, Shield, 
  ArrowLeft, RefreshCw, CheckCircle, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { OFFICIAL_SEAL } from '@/lib/brand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Public login roles - Student and Instructor only
// Administrator access is via /internal/admin
const roleOptions = [
  { 
    id: 'student', 
    label: 'Student', 
    icon: GraduationCap, 
    description: 'Access courses, assignments, and grades',
    color: 'bg-blue-500',
    route: '/student/dashboard'
  },
  { 
    id: 'instructor', 
    label: 'Instructor', 
    icon: Users, 
    description: 'Manage courses, grade submissions',
    color: 'bg-emerald-500',
    route: '/instructor/dashboard'
  }
];

export default function LoginPage() {
  const [step, setStep] = useState('role'); // role, email, verify
  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  const otpRefs = useRef([]);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setError('');
    setStep('email');
  };

  // Request OTP code
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/portal/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role: selectedRole.id })
      });

      // Handle rate limiting (429) before trying to parse JSON
      if (response.status === 429) {
        throw new Error('Please wait before requesting another code.');
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, provide a generic error
        if (!response.ok) {
          throw new Error('Request failed. Please try again.');
        }
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send verification code');
      }

      setSuccess('Verification code sent! Check your email.');
      setStep('verify');
      setCountdown(60); // 60 second cooldown for resend
      
      // Focus first OTP input
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only keep last digit
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5 && newOtp.every(d => d)) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  // Handle backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Paste handler
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newOtp = pasted.split('');
      setOtp(newOtp);
      otpRefs.current[5]?.focus();
      handleVerifyOTP(pasted);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (code = null) => {
    const otpCode = code || otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/portal/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: otpCode, role: selectedRole.id })
      });

      // Handle common error status codes before trying to parse JSON
      if (response.status === 400) {
        let data;
        try {
          data = await response.json();
          throw new Error(data.detail || 'Invalid code. Please try again.');
        } catch (parseError) {
          if (parseError.message.includes('Invalid') || parseError.message.includes('code')) {
            throw parseError;
          }
          throw new Error('Invalid code. Please try again.');
        }
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      // Store token
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('portal_role', data.portal_role);
      
      // Refresh auth state
      await checkAuth();
      
      // Redirect to dashboard
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => {
        navigate(data.redirect_to || selectedRole.route, { replace: true });
      }, 500);
    } catch (err) {
      setError(err.message);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (countdown > 0) return;
    
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/portal/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role: selectedRole.id })
      });

      // Handle rate limiting (429) before trying to parse JSON
      if (response.status === 429) {
        throw new Error('Please wait before requesting another code.');
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        if (!response.ok) {
          throw new Error('Request failed. Please try again.');
        }
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend code');
      }

      setSuccess('New code sent!');
      setOtp(['', '', '', '', '', '']);
      setCountdown(60);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Go back
  const handleBack = () => {
    setError('');
    setSuccess('');
    if (step === 'verify') {
      setStep('email');
      setOtp(['', '', '', '', '', '']);
    } else if (step === 'email') {
      setStep('role');
      setSelectedRole(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="login-page">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg relative z-10"
      >
        <Card className="border-0 bg-white/95 backdrop-blur-sm shadow-2xl">
          <CardHeader className="text-center pb-2">
            <Link to="/" className="inline-block mb-3">
              <img 
                src={OFFICIAL_SEAL}
                alt="Zodiac Workforce Intelligence Institute"
                className="h-16 mx-auto drop-shadow-[0_4px_12px_rgba(15,30,71,0.18)]"
                data-testid="login-seal"
              />
            </Link>
            {/* Institutional Wordmark — consistent with global header */}
            <div className="mb-4">
              <div className="zodiac-heading text-[#0F1E47] font-bold text-2xl tracking-tight leading-none">ZODIAC</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#991B1B] font-semibold mt-1">
                Workforce Intelligence Institute<sup className="text-[7px] ml-0.5 align-super">™</sup>
              </div>
            </div>
            <CardTitle className="font-heading text-2xl text-slate-900">
              {step === 'role' && 'Access the Institute Portal'}
              {step === 'email' && `Login as ${selectedRole?.label}`}
              {step === 'verify' && 'Enter Verification Code'}
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              {step === 'role' && 'Select your role to continue'}
              {step === 'email' && 'Enter your email to receive a verification code'}
              {step === 'verify' && `Code sent to ${email}`}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Back button */}
            {step !== 'role' && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}

            {/* Error/Success Messages */}
            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
              {success && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2 text-emerald-700 text-sm"
                >
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Step 1: Role Selection */}
            <AnimatePresence mode="wait">
              {step === 'role' && (
                <motion.div
                  key="role"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-3"
                >
                  <Label className="text-slate-700 font-medium">Login as:</Label>
                  <div className="grid gap-3">
                    {roleOptions.map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => handleRoleSelect(role)}
                        className="relative p-4 rounded-xl border-2 border-slate-200 hover:border-[#1E3A8A] hover:bg-slate-50 transition-all duration-200 flex items-center gap-4 text-left group"
                        data-testid={`role-select-${role.id}`}
                      >
                        <div className={`w-12 h-12 ${role.color} rounded-xl flex items-center justify-center transition-transform group-hover:scale-110`}>
                          <role.icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-slate-900 block">{role.label}</span>
                          <span className="text-sm text-slate-500">{role.description}</span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#1E3A8A] transition-colors" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 2: Email Input */}
              {step === 'email' && (
                <motion.div
                  key="email"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <form onSubmit={handleRequestOTP} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-11 py-6 text-lg border-slate-200 focus:border-[#1E3A8A] focus:ring-[#1E3A8A]"
                          required
                          autoFocus
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className={`w-full py-6 text-lg ${selectedRole?.color || 'bg-[#1E3A8A]'} hover:opacity-90 text-white`}
                      disabled={isLoading}
                      data-testid="send-code-btn"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending Code...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Send Verification Code
                          <ArrowRight className="w-5 h-5" />
                        </span>
                      )}
                    </Button>
                  </form>

                  {selectedRole?.id !== 'student' && (
                    <p className="text-xs text-slate-500 text-center mt-4">
                      {selectedRole?.label} accounts must be created by an administrator
                    </p>
                  )}
                </motion.div>
              )}

              {/* Step 3: OTP Verification */}
              {step === 'verify' && (
                <motion.div
                  key="verify"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-6"
                >
                  {/* OTP Input */}
                  <div className="space-y-4">
                    <Label className="text-slate-700 text-center block">Enter 6-digit code</Label>
                    <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => otpRefs.current[index] = el}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none transition-all"
                          data-testid={`otp-input-${index}`}
                        />
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={() => handleVerifyOTP()}
                    className={`w-full py-6 text-lg ${selectedRole?.color || 'bg-[#1E3A8A]'} hover:opacity-90 text-white`}
                    disabled={isLoading || otp.some(d => !d)}
                    data-testid="verify-code-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Verifying...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Verify & Login
                        <CheckCircle className="w-5 h-5" />
                      </span>
                    )}
                  </Button>

                  {/* Resend Code */}
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-2">Didn't receive the code?</p>
                    <button
                      onClick={handleResend}
                      disabled={countdown > 0 || isLoading}
                      className={`text-sm font-medium flex items-center gap-2 mx-auto ${
                        countdown > 0 ? 'text-slate-400' : 'text-[#1E3A8A] hover:underline'
                      }`}
                      data-testid="resend-code-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                      {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 text-center">
                    Code expires in 10 minutes. Check your spam folder if you don't see the email.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-white/70 hover:text-white text-sm transition-colors">
            ← Back to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
